import { useState } from "react";
import {
    Button,
    Input,
    NonCancelableCustomEvent,
    SpaceBetween,
    TokenGroup,
    TokenGroupProps,
} from "@amzn/awsui-components-react";
import FormFieldWrapper from "./FormFieldWrapper/FormFieldWrapper";
import { CustomArrayInputProps } from "interfaces";
import { BaseKeyDetail, ClickDetail } from "@amzn/awsui-components-react/polaris/internal/events";
import { trackCustomEvent, useTrackerId } from "@amzn/uxm-aws-ui-components-react/lib/src/uxm/custom-event";

/**
 * A FormField that provides a text input
 *
 * @param {string} label - The form field label
 * @param {string} value - The current input value
 * @param {function} setValue - Function to update the current input value
 *
 *
 * @param {string} placeholder - Placeholder text for the date input
 * @param {boolean} disabled - Whether the input is disabled
 * @param {string} errorText - Error message to display
 * @param {string} warningText - Warning message to display
 * @param {string} description - Description text for the field
 * @param {React.ReactNode} info - Additional info to display
 * @param {string} [testId] - Test identifier for the component
 * @param {InputProps.Type} [type] - The type of the input
 * @param {function} onEnter - Function to be called when the user presses the enter key
 * @param {function} validateInput - Optional validation callback that returns an error message or undefined
 * @param {function} sanitizeInput - Optional sanitization callback that transforms input before adding
 *
 * @returns {JSX.Element} A FormField component with a Select
 */
const CustomArrayInput = ({
    label,
    value,
    setValue,
    placeholder,
    disabled = false,
    errorText,
    warningText,
    description,
    info,
    testId,
    type,
    buttonText,
    touchpoint,
    validateInput,
    sanitizeInput,
}: CustomArrayInputProps) => {
    const [input, setInput] = useState<string>("");
    const [inputError, setInputError] = useState<string>("");

    const trackerId = useTrackerId();

    const handleOnDismiss = (event: NonCancelableCustomEvent<TokenGroupProps.DismissDetail>) => {
        const detail = event.detail;
        setValue([...value.slice(0, detail.itemIndex), ...value.slice(detail.itemIndex + 1)]);

        trackCustomEvent(
            {
                action: "array-item-remove",
                itemIndex: detail.itemIndex,
            },
            touchpoint,
            trackerId,
        );
    };

    const handleOnKeyDown = (event: CustomEvent<BaseKeyDetail>) => {
        if (event.detail.key == "Enter") {
            event.preventDefault();
            addOptionToArray();
        }
    };

    const handleOnSubmit = (event: CustomEvent<ClickDetail>) => {
        event.preventDefault();
        addOptionToArray();
    };

    const addOptionToArray = () => {
        // Clear any previous input errors
        setInputError("");

        // Apply sanitization if provided, otherwise use input as-is
        const processedInput = sanitizeInput ? sanitizeInput(input) : input;

        // Apply validation if provided
        if (validateInput) {
            const validationError = validateInput(processedInput, value);
            if (validationError) {
                setInputError(validationError);
                trackCustomEvent(
                    {
                        action: "array-item-validation-failed",
                        value: input,
                    },
                    touchpoint,
                    trackerId,
                );
                return;
            }
        }

        // Add the processed input to the array
        setValue([...value, processedInput]);
        setInput("");

        trackCustomEvent(
            {
                action: "array-item-add",
                value: processedInput,
            },
            touchpoint,
            trackerId,
        );
    };

    // Clear input error when user changes the input
    const handleInputChange = (detail: { value: string }) => {
        setInput(detail.value);
        // Clear error when user starts typing
        if (inputError) {
            setInputError("");
        }
    };

    const selectedInput = (
        <>
            <SpaceBetween size="m">
                <SpaceBetween size="xs">
                    <Input
                        type={type}
                        onChange={({ detail }) => handleInputChange(detail)}
                        value={input}
                        placeholder={placeholder}
                        disabled={disabled}
                        onKeyDown={handleOnKeyDown}
                        invalid={!!inputError}
                    />
                    <Button onClick={handleOnSubmit}>{buttonText ? buttonText : "Add"}</Button>
                </SpaceBetween>
                {inputError && <div data-testid="array-item-validation-failed-message" style={{ color: "#d91515", fontSize: "12px" }}>{inputError}</div>}
                <TokenGroup
                    data-testid="watcher-list"
                    onDismiss={handleOnDismiss}
                    items={value.map((option) => ({
                        label: option,
                        dismissLabel: `Remove option ${option}`,
                    }))}
                />
            </SpaceBetween>
        </>
    );

    return (
        <FormFieldWrapper
            testId={testId}
            label={label}
            errorText={errorText}
            warningText={warningText}
            description={description}
            info={info}
            input={selectedInput}
        />
    );
};

export default CustomArrayInput;
